// frontend/src/apiClient.js
import axios from "axios";
import impersonationStore from "./utils/impersonationStore";
import { buildLoginPath, isWithinLoginGrace, LOGIN_AT_KEY } from "./utils/auth";

/* -----------------------------------------------------------
   1. Decide which backend URL to use based on environment
   ----------------------------------------------------------- */
const runningLocally =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

// Determine backend URL based on environment
const getBackendURL = () => {
  // Priority 1: Explicit env variable (allows manual override)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  // Priority 2: Environment-based URL selection
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV;

  // If deployed (not localhost), use the current hostname with HTTPS
  if (!runningLocally) {
    return `https://${window.location.hostname}`;
  }

  // Running locally - use environment-specific URLs
  // For staging/production, connect to the real servers instead of localhost
  switch (env) {
    case "staging":
      return "https://staging.cafuamanagement.com";
    case "production":
      return "https://peopleplan.cafuamanagement.com";
    default:
      // Development mode - use local backend
      return "http://localhost:5000";
  }
};

const baseURL = getBackendURL();

/* -----------------------------------------------------------
   2. Create the Axios instance
   ----------------------------------------------------------- */
const apiClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    // ⚠️  omit Cache-Control in dev/staging to avoid CORS issues
    ...(runningLocally
      ? {}
      : { "Cache-Control": "no-cache", Pragma: "no-cache" }),
  },
  validateStatus: (status) => status >= 200 && status < 300,
});

/* -----------------------------------------------------------
   3. Request interceptor – inject token, prefix /api, bust cache
   ----------------------------------------------------------- */
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Ensure every path starts with /api unless it's an absolute URL
  if (!config.url.startsWith("/api") && !config.url.startsWith("http")) {
    config.url = "/api" + config.url;
  }

  // Inject impersonation headers when active
  if (impersonationStore.mode === "user" && impersonationStore.userId) {
    config.headers["X-Impersonate-User-Id"] = String(impersonationStore.userId);
  } else if (impersonationStore.mode === "role" && impersonationStore.role) {
    config.headers["X-Impersonate-Role"] = impersonationStore.role;
  }

  // Add cache-buster only in production
  if (config.method === "get" && !runningLocally) {
    config.params = { ...(config.params || {}), _t: Date.now() };
  }

  if (runningLocally) {
    // Helpful debug in the browser console
    console.debug(
      `[DEV] → ${config.method?.toUpperCase()} ${baseURL}${config.url}`
    );
  }

  return config;
});

/* -----------------------------------------------------------
   4. Response interceptor – 401 handling
   ----------------------------------------------------------- */
apiClient.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err.config?.url;
    const status = err.response?.status;
    console.error("API Error:", url, status, err.response?.data);

    if (status === 401) {
      // Don't redirect if we're already on the login page or just logged in
      const isLoginPage = window.location.pathname === '/' || window.location.pathname === '';
      const isLoginEndpoint = url?.includes('/login');

      console.log("[401 Handler] isLoginPage:", isLoginPage, "isLoginEndpoint:", isLoginEndpoint, "url:", url);

      // Skip redirect for login-related requests (let the login page handle the error)
      if (isLoginPage || isLoginEndpoint) {
        console.log("[401 Handler] Skipping redirect - on login page or login endpoint");
        return Promise.reject(err);
      }

      // Ignore a 401 that races a brand-new login (within 10 seconds of it).
      // This reads the login timestamp, NOT lastActivity: useSessionTimeout
      // rewrites lastActivity on every page load and on user activity, which
      // made an expired session's 401s look "just logged in" and suppressed
      // the redirect, most reliably right after a reload.
      if (isWithinLoginGrace()) {
        console.warn("[401 Handler] Ignoring 401 - user just logged in, possible race condition. URL:", url);
        return Promise.reject(err);
      }

      console.log("[401 Handler] Clearing session and redirecting to login");
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("role");
      localStorage.removeItem("userId");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("userName");
      localStorage.removeItem("lastActivity");
      localStorage.removeItem(LOGIN_AT_KEY);
      // Redirect to login, preserving the current URL (+query) as ?returnUrl=
      // so the user lands back where they were after re-authenticating. A query
      // param is the only carrier that survives this hard redirect (it destroys
      // any in-memory React Router state).
      const loginPath = buildLoginPath(window.location.pathname + window.location.search);
      const sep = loginPath.includes("?") ? "&" : "?";
      window.location.href = `${loginPath}${sep}error=${encodeURIComponent("Session expired, please login again")}`;
    }
    return Promise.reject(err);
  }
);

export default apiClient;